import { gsap, ScrollTrigger } from "./gsapSetup";

import initMouseStars from "./initMouseStars";
import runGsapLogic, {prepareAboveFold, playAboveFold} from "./gsapLogic";
import initServiceCardHover, { initServiceTilesHover } from "./initServiceCardHover";
import {prepareBackgroundPositionAnim, playBackgroundPositionAnim} from "./initBackgroundPositionAnim";
import {gateScrollAnimationsUntilLoaderDone, initScrollFillText} from "./gsapColorTextOnScroll";
import {initWebBackground} from "./gsapWebBackground";
import {initCursorBadge} from "./gsapMouseSvgMove";
import {initGsapDigitCountersOnScroll} from "./gsapAnimateNumbers";
import {initTitleLetters} from "./gsapTitleLetters";
import {initUiButtonHoverFill} from "./gsapButtonHoverAnim";

function getBgImageUrl(el) {
  if (!el) return null;
  const bg = getComputedStyle(el).backgroundImage;
  if (!bg || bg === "none") return null;
  const match = bg.match(/url\(["']?(.*?)["']?\)/);
  return match?.[1] || null;
}

function preloadImage(url) {
  return new Promise((resolve) => {
    if (!url) return resolve(false);
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

function tweenToPromise(tween) {
  return new Promise((resolve) => tween.eventCallback("onComplete", resolve));
}

function buildLoaderIntro({ row, logo, nameInner }) {
  gsap.set(logo, { transformOrigin: "50% 50%", transformBox: "fill-box" });
  gsap.set(nameInner, { transformOrigin: "0% 50%", transformBox: "fill-box" });

  // Measure wordmark width (vector-accurate)
  const nameBox = nameInner.getBBox();
  const halfWordmarkWidth = nameBox.width / 2;

  // ✅ Keep row centered; compensate by pushing only the logo to the right
  gsap.set(row, { x: 0 });

  // Logo starts centered visually (shifted right), then will move left to its final spot (x: 0)
  gsap.set(logo, { scale: 0, x: halfWordmarkWidth, autoAlpha: 1 });

  // Wordmark starts hidden and slightly “inside” the logo, then moves right to x: 0
  gsap.set(nameInner, { x: -18, autoAlpha: 0 });

  const tl = gsap.timeline();

  // 1) Logo bounce (still centered)
  tl.to(logo, { scale: 1.3, duration: 0.6, ease: "power3.out" })
    .to(logo, { scale: 1.0, duration: 0.35, ease: "power2.out" }, ">")
    .to({}, { duration: 0.12 });

  // 2) Reveal: logo goes LEFT, text goes RIGHT
  tl.to(logo, { x: 0, duration: 0.45, ease: "power2.out" }, ">")
    .to(nameInner, { x: 0, autoAlpha: 1, duration: 0.55, ease: "power3.out" }, "<");

  return tl;
}


const runAnimations = () => {
  // re-enable ScrollTrigger
  window.dispatchEvent(new Event("loader:done"));
  runGsapLogic();
  initTitleLetters();
  initServiceCardHover();
  initServiceTilesHover();
  playBackgroundPositionAnim();
  initScrollFillText();
  initGsapDigitCountersOnScroll();
  initWebBackground();
  initCursorBadge();
  initUiButtonHoverFill();

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      ScrollTrigger.refresh(true);
    });
  });
}


export async function initPageLoaderAndAnimations() {
  const loader = document.querySelector("#page-loader");
  const row = document.querySelector("#loader-row");
  const logo = row?.querySelector("#loader-logo");
  const nameInner = row?.querySelector("#loader-name-inner");
  const hero = document.querySelector("[data-hero]");

  prepareBackgroundPositionAnim();
  gateScrollAnimationsUntilLoaderDone();
  initMouseStars(); // before main loader ends

  if (!loader || !row || !logo || !nameInner) {
    runAnimations();
    return;
  }

  // document.documentElement.classList.add("overflow-hidden");
  document.body.classList.add("is-loading");

  const heroBgUrl = hero ? getBgImageUrl(hero) : null;

  // Start loader intro immediately (now row exists and is passed)
  const introTl = buildLoaderIntro({ row, logo, nameInner });

  const assetsReady = Promise.all([
    preloadImage(heroBgUrl),
    document.fonts?.ready?.catch(() => {}),
    // new Promise(r => setTimeout(r, 100000))
  ]);

  const introDone = new Promise((resolve) => introTl.eventCallback("onComplete", resolve));
  await Promise.all([assetsReady, introDone]);

  prepareAboveFold();

  // Hide loader (await safely)
  await tweenToPromise(gsap.to(loader, { autoAlpha: 0, duration: 0.35, ease: "power2.out" }));

  loader.remove();
  // document.documentElement.classList.remove("overflow-hidden");
  document.body.classList.remove("is-loading");

  playAboveFold();

  runAnimations();
}
